import * as yup from 'yup';
import { calculateDiff, getOlderApplicantBasedOnDateOfBirth } from 'utils/date';
import {
  nullableNumber,
  nullableString,
  optionalStringValidationSchema,
  percentageValidation,
  positiveCurrencyIncludingZeroValidation,
  positiveCurrencyValidation,
  postcodeValidation,
} from 'utils/form/validation';
import { CREDIT_COMMITMENT_TYPES } from '../../../CalculationId.types';
import { liabilityTypeDirectorFactory } from '../../../utils/liabilities';
import { DataDirector } from '../../../utils/liabilities/types';
import { ApplicantFormType, FIELDS, FormValues } from '../Form.types';
import { dateValidation } from 'utils/date';
import { Option } from 'shared/interface/globalInterface';
import { ProductRepaymentType, ProductType, ProductTypes } from 'domain/Product';
import { ukPostalCode } from 'shared/regex/regex';
import { CheckRegionAvailabilityForTioError } from 'modules/business_rules/api/checkRegionAvailabilityForTio';
import { Nullable } from 'utils/types';
import {
  checkPostcode,
  mortgageTermByAgeTestOptions,
  ltvForProductTestOptions,
  minimumRemainingEquityTestOptions,
  fullAgeValidation,
  mortgageTermByAgeForApplicantValidation,
} from './businessRulesValidations';

const messages = {
  required: 'This field is required',
};

const testPension = function(this: yup.TestContext, val: any, prefix: string): true | yup.ValidationError {
  const path = FIELDS.STATE_PENSION;
  const anotherStatePenisonValue = this.parent[prefix][path];

  return ![anotherStatePenisonValue, val[FIELDS.STATE_PENSION]].includes(0)
    ? this.createError({
        path: `${this.path}.${path}`,
        message: 'Only one state pension (Current/Future) can be provided.',
      })
    : true;
};

const typeValidatorsCache = new Map<CREDIT_COMMITMENT_TYPES, DataDirector>();

const shouldValidateCreditCommitment = (type: CREDIT_COMMITMENT_TYPES, field: FIELDS) => {
  let typeValidator = typeValidatorsCache.get(type);
  if (!typeValidator) {
    typeValidator = liabilityTypeDirectorFactory(type);
    typeValidatorsCache.set(type, typeValidator);
  }
  const allowedFields = typeValidator.getAllowedFields();

  return allowedFields.includes(field);
};

const otherIncomeSchema = yup
  .object()
  .shape<ApplicantFormType[FIELDS.CURRENT_INCOME_PREFIX][FIELDS.OTHER_INCOME_PREFIX]>({
    [FIELDS.INVESTMENT_AND_SAVINGS]: positiveCurrencyIncludingZeroValidation().required(messages.required),
    [FIELDS.RENTAL_INCOME]: positiveCurrencyIncludingZeroValidation().required(messages.required),
    [FIELDS.MAINTENANCE_INCOME]: positiveCurrencyIncludingZeroValidation().required(messages.required),
    [FIELDS.FOSTERING_INCOME]: positiveCurrencyIncludingZeroValidation().required(messages.required),
    [FIELDS.BENEFIT_INCOME]: positiveCurrencyIncludingZeroValidation().required(messages.required),
    [FIELDS.GUARANTEED_BENEFIT]: yup
      .boolean()
      .nullable()
      .required(messages.required),
    [FIELDS.GUARANTEED_MAINTENANCE]: yup
      .boolean()
      .nullable()
      .required(messages.required),
  });

const currentIncomeSchema = yup.object().shape<ApplicantFormType[FIELDS.CURRENT_INCOME_PREFIX]>({
  [FIELDS.BUSINESS_TYPE]: nullableString().when(FIELDS.SELF_EMPLOYED_SALARY, {
    is: (val: number) => val > 0,
    then: yup.string().required(`${messages.required} when self-employed income is provided.`),
  }),
  [FIELDS.EMPLOYED_GROSS_SALARY]: positiveCurrencyIncludingZeroValidation().required(messages.required),
  [FIELDS.SELF_EMPLOYED_SALARY]: positiveCurrencyIncludingZeroValidation().required(messages.required),
  [FIELDS.OTHER_INCOME_PREFIX]: otherIncomeSchema,
  [FIELDS.RELATED_TO_EMPLOYED_GROSS_SALARY]: yup.object().shape({
    [FIELDS.BONUS_INCOME]: positiveCurrencyIncludingZeroValidation().required(messages.required),
    [FIELDS.COMMISSION_INCOME]: positiveCurrencyIncludingZeroValidation().required(messages.required),
    [FIELDS.OVERTIME_INCOME]: positiveCurrencyIncludingZeroValidation().required(messages.required),
    [FIELDS.SHIFT_ALLOWANCE_INCOME]: positiveCurrencyIncludingZeroValidation().required(messages.required),
    [FIELDS.IS_GUARANTEED_BONUS_INCOME]: yup.boolean(),
    [FIELDS.IS_GUARANTEED_COMMISSION_INCOME]: yup.boolean(),
    [FIELDS.IS_GUARANTEED_OVERTIME_INCOME]: yup.boolean(),
    [FIELDS.IS_GUARANTEED_SHIFT_ALLOWANCE_INCOME]: yup.boolean(),
  }),
  [FIELDS.RELATED_TO_SELF_EMPLOYED_SALARY]: yup.object().shape({
    [FIELDS.DIVIDENTS_INCOME]: positiveCurrencyIncludingZeroValidation().required(messages.required),
  }),
});

const futureIncomeSchema = yup.object().shape<ApplicantFormType[FIELDS.FUTURE_INCOME_PREFIX]>({
  [FIELDS.HAS_TAXABLE_INCOME_CONTINUING]: yup.boolean(),
  [FIELDS.MIN_MONTHLY_TAXABLE_RETIRED_INCOME]: nullableNumber().when(FIELDS.HAS_TAXABLE_INCOME_CONTINUING, {
    is: false,
    then: positiveCurrencyIncludingZeroValidation().required(messages.required),
  }),
  [FIELDS.MIN_MONTHLY_TAX_FREE_RETIRED_INCOME]: nullableNumber().when(FIELDS.HAS_TAX_FREE_INCOME_CONTINUING, {
    is: false,
    then: positiveCurrencyIncludingZeroValidation().required(messages.required),
  }),
  [FIELDS.HAS_TAX_FREE_INCOME_CONTINUING]: yup.boolean(),
});

const lifeEventsSchema = yup.object().shape<ApplicantFormType[FIELDS.LIFE_EVENTS_PREFIX]>({
  [FIELDS.INVESTMENT_PORTFOLIO_VALUE]: positiveCurrencyIncludingZeroValidation().required(messages.required),
  [FIELDS.VALUE_OF_ASSET_AT_RETIREMENT]: positiveCurrencyIncludingZeroValidation().required(messages.required),
  [FIELDS.MORTGAGE_PROTECTION_MONTHLY_PAYMENT]: positiveCurrencyIncludingZeroValidation().required(messages.required),
  [FIELDS.LIFE_INSURANCE_LUMP_SUM]: positiveCurrencyIncludingZeroValidation().required(messages.required),
  [FIELDS.LIFE_INSURANCE_PREMIUM]: positiveCurrencyIncludingZeroValidation().required(messages.required),
});

const currentPensionSchema = yup
  .object()
  .shape<ApplicantFormType[FIELDS.CURRENT_PENSION_PREFIX]>({
    [FIELDS.STATE_PENSION]: positiveCurrencyIncludingZeroValidation(),
    [FIELDS.DEFINED_BENEFIT]: positiveCurrencyIncludingZeroValidation().required(messages.required),
    [FIELDS.PER_PASSED_ON_DEATH_NEXT_TO_DEF_BENEFIT]: percentageValidation().required(messages.required),
    [FIELDS.DEFINED_CONTRIBUTION]: positiveCurrencyIncludingZeroValidation().required(messages.required),
    [FIELDS.PER_PASSED_ON_DEATH_NEXT_TO_DEF_CONTRIBUTION]: percentageValidation().required(messages.required),
    [FIELDS.ANNUITY]: positiveCurrencyIncludingZeroValidation().required(messages.required),
    [FIELDS.PER_PASSED_ON_DEATH_NEXT_TO_ANNUITY]: percentageValidation().required(messages.required),
  })
  .test('has-only-one-pension-provided', '', function(val: any) {
    return testPension.call(this, val, FIELDS.FUTURE_PENSION_PREFIX);
  });

const futurePensionSchema = yup
  .object()
  .shape<ApplicantFormType[FIELDS.FUTURE_PENSION_PREFIX]>({
    [FIELDS.STATE_PENSION]: positiveCurrencyIncludingZeroValidation().required(messages.required),
    [FIELDS.DEFINED_BENEFIT]: positiveCurrencyIncludingZeroValidation().required(messages.required),
    [FIELDS.PER_TRANSFERRED_ON_DEATH_UNDER_DEF_BENEFIT]: percentageValidation().required(messages.required),
    [FIELDS.REPAYMENT_LUMP_SUM_UNDER_DEFINED_BENEFIT]: positiveCurrencyIncludingZeroValidation().required(
      messages.required,
    ),
    [FIELDS.USE_OF_FUNDS]: optionalStringValidationSchema(
      FIELDS.DEFINED_CONTRIBUTION,
      (val) => val > 0,
      nullableString().required(messages.required),
    ),
    [FIELDS.DEFINED_CONTRIBUTION]: positiveCurrencyIncludingZeroValidation().required(messages.required),
    [FIELDS.ONGOING_CONTRIBUTIONS]: positiveCurrencyIncludingZeroValidation().required(messages.required),
    [FIELDS.PER_TRANSFERRED_ON_DEATH_UNDER_DEF_CONTRIBUTION]: percentageValidation().required(messages.required),
    [FIELDS.LUMP_SUM_DRAWDOWN]: percentageValidation().required(messages.required),
    [FIELDS.REPAYMENT_LUMP_SUM_UNDER_DEFINED_CONTRIBUTION]: percentageValidation().required(messages.required),
  })
  .test('has-only-one-pension-provided', '', function(val: any) {
    return testPension.call(this, val, FIELDS.CURRENT_PENSION_PREFIX);
  });

const applicantSchema = (
  productType: ProductType | undefined,
  applicant1: ApplicantFormType,
  applicant2: Nullable<ApplicantFormType>,
  termLength: Nullable<number>,
  repaymentType: Nullable<ProductRepaymentType>,
) =>
  yup.object().shape<ApplicantFormType>({
    [FIELDS.CURRENT_INCOME_PREFIX]: currentIncomeSchema,
    [FIELDS.FUTURE_INCOME_PREFIX]: futureIncomeSchema,
    [FIELDS.APPLICANT_DATE_OF_BIRTH]: yup
      .string()
      .nullable()
      .test({
        name: 'Validate age and mortgage term by age for applicant',
        test: async function(value) {
          if (!productType || !value || !repaymentType) {
            return true;
          }

          const ageValidation = await fullAgeValidation(productType, value, repaymentType);

          if (ageValidation !== false) {
            return this.createError({
              message: ageValidation,
            });
          } else {
            if (!applicant1 || !applicant1.dateOfBirth || !applicant2) {
              return true;
            }

            const olderApplicant = getOlderApplicantBasedOnDateOfBirth(applicant1.dateOfBirth, applicant2.dateOfBirth);

            if (!this.path.includes(olderApplicant)) {
              return true;
            }

            const mortgageTermByAgeForApplicant = await mortgageTermByAgeForApplicantValidation(
              productType,
              null,
              applicant1,
              applicant2,
              termLength,
            );

            if (mortgageTermByAgeForApplicant !== false) {
              return this.createError({
                message: mortgageTermByAgeForApplicant,
              });
            }
          }

          return true;
        },
      })
      .required(messages.required),
    [FIELDS.APPLICANT_GENDER]: nullableString().required(messages.required),
    [FIELDS.APPLICANT_MARITAL_STATUS]: nullableString().required(messages.required),
    [FIELDS.APPLICANT_LIFE_STAGE]: nullableString().required(messages.required),
    [FIELDS.APPLICANT_RETIREMENT_AGE]: nullableNumber().when(FIELDS.APPLICANT_LIFE_STAGE, {
      is: 'NOT_RETIRED',
      then: yup
        .number()
        .required(`${messages.required} when applicant is non-retired.`)
        .test(
          'retirement-age-is-greather-than-applicant-age',
          'Retirement age should be greater than current age',
          function(retirementAge) {
            const dateOfBirth = this.resolve(yup.ref(FIELDS.APPLICANT_DATE_OF_BIRTH));

            const age = calculateDiff(dateOfBirth, 'years', false);

            return age < retirementAge;
          },
        ),
    }),
    [FIELDS.LIFE_EVENTS_PREFIX]: lifeEventsSchema,
    [FIELDS.CURRENT_PENSION_PREFIX]: currentPensionSchema,
    [FIELDS.FUTURE_PENSION_PREFIX]: futurePensionSchema,
    [FIELDS.LIABILITY_PREFIX]: yup.array().of(
      yup.object().shape({
        [FIELDS.LIABILITY_TYPE]: yup.string().required(messages.required),
        [FIELDS.LIABILITY_CREDIT_CARD_BALANCE]: nullableNumber().when(FIELDS.LIABILITY_TYPE, {
          is: (type) => shouldValidateCreditCommitment(type, FIELDS.LIABILITY_CREDIT_CARD_BALANCE),
          then: positiveCurrencyIncludingZeroValidation().required(messages.required),
        }),
        [FIELDS.LIABILITY_AMOUNT]: nullableNumber().when(FIELDS.LIABILITY_TYPE, {
          is: (type) => shouldValidateCreditCommitment(type, FIELDS.LIABILITY_AMOUNT),
          then: positiveCurrencyIncludingZeroValidation().required(messages.required),
        }),
        [FIELDS.LIABILITY_COMMITMENT_END_DATE]: nullableString().when(FIELDS.LIABILITY_TYPE, {
          is: (type) => shouldValidateCreditCommitment(type, FIELDS.LIABILITY_COMMITMENT_END_DATE),
          then: dateValidation().test(
            'is-more-than-today',
            'Date of commitment must be later than 1 day from today.',
            (value: string) => {
              const diff = calculateDiff(value, 'day');
              return value ? diff < 0 : true;
            },
          ),
        }),
        [FIELDS.LIABILITY_IS_CLEARED_BEFORE_LOAN_COMPLETES]: yup
          .boolean()
          .nullable()
          .when(FIELDS.LIABILITY_TYPE, {
            is: (type) => shouldValidateCreditCommitment(type, FIELDS.LIABILITY_IS_CLEARED_BEFORE_LOAN_COMPLETES),
            then: yup.boolean().required(messages.required),
          }),
      }),
    ),
  });

const validationSchema = (
  products: Option<{ productType: string; code: string }>[],
  productType: ProductType | undefined,
  applicant1: ApplicantFormType,
  applicant2: Nullable<ApplicantFormType>,
  termLength: Nullable<number>,
  repaymentType: Nullable<ProductRepaymentType>,
) =>
  yup.object().shape<any>({
    [FIELDS.NUMBER_OF_APPLICANTS]: yup
      .number()
      .moreThan(0, messages.required)
      .required(messages.required),
    [FIELDS.LOAN_DETAILS_PREFIX]: yup.object().shape({
      [FIELDS.LOAN_REQUESTED]: positiveCurrencyValidation()
        .required(messages.required)
        .test(ltvForProductTestOptions(products))
        .test(minimumRemainingEquityTestOptions(products)),
      [FIELDS.MONTHLY_LIVING_COSTS]: positiveCurrencyIncludingZeroValidation().required(messages.required),
      [FIELDS.MORTGAGE_PRODUCT]: nullableString().required(messages.required),
      [FIELDS.PRODUCT_REPAYMENT_TYPE]:
        productType === ProductTypes.TIO ? nullableString().required(messages.required) : nullableString(),
      [FIELDS.POST_CODE]: postcodeValidation()
        .nullable()
        .required(messages.required)
        .when(FIELDS.MORTGAGE_PRODUCT, {
          is: (identifier) => {
            const found = products.find((product) => product.value === identifier);

            return found && found.params?.productType === ProductTypes.TIO;
          },
          then: postcodeValidation()
            .nullable()
            .required(messages.required)
            .test({
              name: 'Check region availability for TIO',
              test: async function() {
                const { postCode } = this.parent;

                try {
                  if (ukPostalCode.test(postCode)) {
                    await checkPostcode(postCode);
                  }
                } catch (error) {
                  if (error instanceof CheckRegionAvailabilityForTioError) {
                    return this.createError({
                      path: `${FIELDS.LOAN_DETAILS_PREFIX}.${FIELDS.POST_CODE}`,
                      message: error.message,
                    });
                  }
                }

                return true;
              },
            })
            .required(messages.required),
        })
        .required(messages.required),
      [FIELDS.PROPERTY_VALUE]: positiveCurrencyValidation().required(messages.required),
      [FIELDS.IS_PRIMARY_HOME]: yup.string().nullable(),
      [FIELDS.ANNUAL_COUNCIL_TAX]: positiveCurrencyIncludingZeroValidation().required(messages.required),
      [FIELDS.ANNUAL_GROUND_RENT_SERVICE_CHARGE]: positiveCurrencyIncludingZeroValidation(),
      [FIELDS.TERM_LENGTH]: yup
        .number()
        .nullable()
        .when(FIELDS.MORTGAGE_PRODUCT, {
          is: (identifier) => {
            const found = products.find((product) => product.value === identifier);

            return found && found.params?.productType === ProductTypes.TIO;
          },
          then: yup
            .number()
            .nullable()
            .required(messages.required)
            .test(mortgageTermByAgeTestOptions(productType, applicant1, applicant2)),
        }),
    }),
    [FIELDS.APPLICANT_1]: applicantSchema(productType, applicant1, applicant2, termLength, repaymentType),
    [FIELDS.APPLICANT_2]: yup.object().when(FIELDS.NUMBER_OF_APPLICANTS, {
      is: 2,
      then: applicantSchema(productType, applicant1, applicant2, termLength, repaymentType),
      otherwise: yup.object(),
    }),
  });

const lazySchema = (
  products: Option<{ productType: string; code: string }>[],
  productType: ProductType | undefined,
  repaymentType: Nullable<ProductRepaymentType>,
) =>
  yup.lazy(({ applicant1, applicant2, loanDetails: { termLength } }: FormValues) =>
    validationSchema(products, productType, applicant1, applicant2, termLength, repaymentType),
  );

export default lazySchema;
